import React from 'react';
import AuditLabel from './AuditLabel';
import '../styles/components/image-upload.css';

const BREED_STYLES = [
    { test: (b) => b.includes('pomer'),                     bg: '#FFF0F6', color: '#be185d', emoji: '🐩' },
    { test: (b) => b.includes('dalm'),                      bg: '#EEF7FF', color: '#1d4ed8', emoji: '🐕' },
    { test: (b) => b.includes('french') || b.includes('buldogue') || b.includes('bulldog'), bg: '#FFF5E6', color: '#92400e', emoji: '🐶' },
    { test: (b) => b.includes('labrador') || b.includes(' lab'),   bg: '#EDFAF4', color: '#166534', emoji: '🐕' },
    { test: (b) => b.includes('golden'),                    bg: '#FFF5E6', color: '#92400e', emoji: '🐶' },
];

const getBreedStyle = (breed) => {
    const b = (breed || '').toLowerCase();
    return BREED_STYLES.find(s => s.test(b)) || { bg: '#F0EDFF', color: '#4C35C4', emoji: '🐕' };
};

const DogCard = ({ dog, onEdit, onDelete, onViewDetails }) => {
    const formatAge = (dog) => {
        // Backend populates calculatedAge virtual; fall back to client-side calculation
        // when the API response predates that virtual, then to the legacy numeric age field.
        if (dog.calculatedAge && dog.calculatedAge.displayText) {
            return dog.calculatedAge.displayText;
        }

        if (dog.dateOfBirth) {
            const now = new Date();
            const dob = new Date(dog.dateOfBirth);
            
            if (!isNaN(dob.getTime()) && dob <= now) {
                let years = now.getFullYear() - dob.getFullYear();
                let months = now.getMonth() - dob.getMonth();
                
                if (now.getDate() < dob.getDate()) {
                    months--;
                }
                
                if (months < 0) {
                    years--;
                    months += 12;
                }
                
                if (years === 0) {
                    return months === 0 ? 'Menor de 1 mes' :
                           months === 1 ? '1 mes' : `${months} meses`;
                } else {
                    return years === 1 && months === 0 ? '1 año' :
                           months === 0 ? `${years} años` : `${years} años, ${months} meses`;
                }
            }
        }
        
        const age = dog.age;
        if (!age && age !== 0) return 'Edad desconocida';
        return `${age} año${age !== 1 ? 's' : ''}`;
    };

    const formatWeight = (weight) => {
        if (!weight && weight !== 0) return null;
        return `${weight} kg`;
    };

    const getOwnerNames = (owners) => {
        if (!owners || owners.length === 0) return 'Sin propietarios';
        return owners.map(owner => owner.name).join(', ');
    };

    const getOwnerContacts = (owners) => {
        if (!owners || owners.length === 0) return [];
        return owners.map(owner => ({
            name: owner.name,
            email: owner.email,
            phone: owner.phone
        })).filter(contact => contact.email || contact.phone);
    };

    const formatAgeShort = (dog) => {
        const full = formatAge(dog);
        const yMatch = full.match(/^(\d+)\s+year/);
        if (yMatch) return `${yMatch[1]}y`;
        const mMatch = full.match(/^(\d+)\s+month/);
        if (mMatch) return `${mMatch[1]}mo`;
        if (full.includes('Less than')) return '<1mo';
        return '';
    };

    const ownerNames = getOwnerNames(dog.owners);
    const ownerContacts = getOwnerContacts(dog.owners);
    const breedStyle = getBreedStyle(dog.breed);
    const primaryOwner = dog.owners?.[0];
    const tagText = (dog.breed || 'MIX').substring(0, 8).toUpperCase();
    const metaParts = [dog.breed, formatAgeShort(dog), dog.dogId ? `ID ${dog.dogId}` : null].filter(Boolean);

    return (
        <div className="dog-card">
            {/* ── Mobile compact row (hidden on desktop) ── */}
            <div
                className="dog-card-mobile"
                onClick={() => onViewDetails && onViewDetails(dog)}
            >
                <div className="dog-card-mobile-avatar" style={{ backgroundColor: breedStyle.bg }}>
                    {breedStyle.emoji}
                </div>
                <div className="dog-card-mobile-info">
                    <div className="dog-card-mobile-name">{dog.name || 'Unnamed'}</div>
                    <div className="dog-card-mobile-meta">{metaParts.join(' · ')}</div>
                    {primaryOwner && (
                        <div className="dog-card-mobile-owner">
                            {primaryOwner.name}{primaryOwner.phone ? ` · ${primaryOwner.phone}` : ''}
                        </div>
                    )}
                </div>
                <div className="dog-card-mobile-right">
                    <span className="dog-card-mobile-tag" style={{ backgroundColor: breedStyle.bg, color: breedStyle.color }}>
                        {tagText}
                    </span>
                </div>
            </div>

            {/* ── Desktop card (hidden on mobile) ── */}
            <div className="dog-card-desktop-content">
            <div className="dog-header">
                {(dog.picture || dog.pictureUrl) && (
                    <div className="dog-picture">
                        <img
                            src={dog.pictureUrl || dog.picture}
                            alt={dog.name || 'Perro'}
                            className="dog-picture-img"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    </div>
                )}
                <div className="dog-title">
                    <h3>🐕 {dog.name || 'Sin nombre'}</h3>
                    {dog.dogId && <span className="dog-id">ID: {dog.dogId}</span>}
                </div>
                <div className="dog-breed-badge">
                    {dog.breed || 'Raza desconocida'}
                </div>
            </div>
            
            <div className="dog-info">
                <p className="dog-name">Nombre: {dog.name || 'Sin nombre'}</p>
                <p className="dog-breed">Raza: {dog.breed || 'Desconocida'}</p>
                <p className="dog-age">Edad: {formatAge(dog)}</p>
                {formatWeight(dog.weight) && (
                    <p className="dog-weight">Peso: {formatWeight(dog.weight)}</p>
                )}
                <p className="dog-owners">Propietario(s): {ownerNames}</p>
                {dog.particularSigns && (
                    <p className="dog-particular-signs">
                        Señas: {dog.particularSigns.length > 50
                            ? `${dog.particularSigns.substring(0, 50)}...`
                            : dog.particularSigns}
                    </p>
                )}
            </div>

            {ownerContacts.length > 0 && (
                <div className="dog-contacts">
                    <strong>Contacto:</strong>
                    {ownerContacts.map((contact, index) => (
                        <div key={index} className="owner-contact">
                            <span className="contact-name">{contact.name}:</span>
                            {contact.email && (
                                <span className="contact-email">{contact.email}</span>
                            )}
                            {contact.phone && (
                                <span className="contact-phone">{contact.phone}</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
            
            {/* Audit Trail */}
            <AuditLabel
                createdBy={dog.createdBy}
                updatedBy={dog.updatedBy}
                createdAt={dog.createdAt}
                updatedAt={dog.updatedAt}
                size="small"
            />
            
            <div className="card-actions">
                {onViewDetails && (
                    <button
                        onClick={() => onViewDetails(dog)}
                        className="view-details-btn"
                        title="Ver información detallada"
                    >
                        Ver detalles
                    </button>
                )}

                {onEdit && (
                    <button
                        onClick={() => onEdit(dog)}
                        className="edit-btn"
                        title="Editar información del perro"
                    >
                        Editar
                    </button>
                )}

                {onDelete && (
                    <button
                        onClick={() => onDelete(dog._id)}
                        className="delete-btn"
                        title="Eliminar perro del sistema"
                    >
                        Eliminar
                    </button>
                )}
            </div>
            </div>{/* end dog-card-desktop-content */}
        </div>
    );
};

export default DogCard;